import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/misc'
import { NativeSelect } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  DOCUMENT_TYPES,
  documentFileUrl,
  getDocument,
  updateDocument,
  type DocumentDetail,
  type DocumentPatch,
  type DocumentType,
} from '@/lib/api/documents/get-documents'
import { apiErrorMessage } from '@/lib/api-client'
import { useDocumentTypeLabel } from '@/lib/document-types'
import { cn, formatDate } from '@/lib/utils'

/**
 * Edit a document's metadata — every column of the EKI source table
 * (source_metadata.xlsx) plus the few fields the app had on top (short name,
 * publication). A modal rather than the old inline row: thirteen fields do
 * not fit in a 420px list row, and the list should not reflow while typing.
 *
 * The full record is fetched on open; the list only carries what its rows
 * display, and this is the one place that needs the rest.
 */
export function DocumentMetadataDialog({
  docId,
  onClose,
  onSaved,
}: {
  /** null = closed. */
  docId: number | null
  onClose: () => void
  /** After a successful save (the caller refreshes its list). */
  onSaved: (doc: DocumentDetail) => void
}) {
  const { t } = useTranslation()
  const detail = useQuery({
    queryKey: ['document', docId],
    queryFn: () => getDocument(docId!),
    enabled: docId !== null,
  })

  return (
    <Dialog open={docId !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent closeLabel={t('common.close')}>
        <DialogHeader>
          <DialogTitle>{t('documents.meta.title')}</DialogTitle>
          <DialogDescription>{t('documents.meta.description')}</DialogDescription>
        </DialogHeader>
        {detail.data ? (
          // Keyed by id so a different document starts from its own values
          // instead of an effect syncing state after the first render.
          <MetadataForm key={detail.data.id} doc={detail.data} onCancel={onClose} onSaved={onSaved} />
        ) : detail.isError ? (
          <DialogBody>
            <p className="text-xs text-danger">
              {t('documents.meta.loadFailed')}: {apiErrorMessage(detail.error)}
            </p>
          </DialogBody>
        ) : (
          <DialogBody className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </DialogBody>
        )}
      </DialogContent>
    </Dialog>
  )
}

interface FormState {
  title: string
  short_name: string
  document_type: DocumentType
  author: string
  publisher: string
  publication: string
  publication_year: string
  languages: string
  is_translation: boolean
  url: string
  date_accessed: string
  licence: string
  is_valid: boolean
  valid_until: string
  comments: string
}

function fromDetail(doc: DocumentDetail): FormState {
  const type = doc.document_type ?? 'other'
  return {
    title: doc.title,
    short_name: doc.short_name ?? '',
    document_type: (DOCUMENT_TYPES as readonly string[]).includes(type)
      ? (type as DocumentType)
      : 'other',
    author: doc.author ?? '',
    publisher: doc.publisher ?? '',
    publication: doc.publication ?? '',
    publication_year: doc.publication_year ? String(doc.publication_year) : '',
    languages: doc.languages ?? '',
    is_translation: doc.is_translation,
    url: doc.url ?? '',
    // Dates come back as ISO; <input type="date"> wants exactly YYYY-MM-DD.
    date_accessed: (doc.date_accessed ?? '').slice(0, 10),
    licence: doc.licence ?? '',
    is_valid: doc.is_valid,
    valid_until: (doc.valid_until ?? '').slice(0, 10),
    comments: doc.comments ?? '',
  }
}

/** Every field is sent, so a cleared input clears the column. */
function toPatch(f: FormState): DocumentPatch {
  const year = f.publication_year.trim()
  return {
    title: f.title.trim(),
    short_name: f.short_name.trim(),
    document_type: f.document_type,
    author: f.author.trim(),
    publisher: f.publisher.trim(),
    publication: f.publication.trim(),
    publication_year: year ? Number(year) : null,
    languages: f.languages
      .split(',')
      .map((l) => l.trim().toLowerCase())
      .filter(Boolean)
      .join(','),
    is_translation: f.is_translation,
    url: f.url.trim(),
    date_accessed: f.date_accessed,
    licence: f.licence.trim(),
    is_valid: f.is_valid,
    valid_until: f.valid_until,
    comments: f.comments.trim(),
  }
}

function MetadataForm({
  doc,
  onCancel,
  onSaved,
}: {
  doc: DocumentDetail
  onCancel: () => void
  onSaved: (doc: DocumentDetail) => void
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const typeLabel = useDocumentTypeLabel()
  const [form, setForm] = useState<FormState>(() => fromDetail(doc))
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }))

  const save = useMutation({
    mutationFn: () => updateDocument(doc.id, toPatch(form)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['document', doc.id] })
      qc.invalidateQueries({ queryKey: ['documents', doc.collection_id ?? null] })
      toast.success(t('common.saved'))
      onSaved(doc)
    },
    onError: (e) =>
      toast.error(t('documents.saveFailed'), { description: apiErrorMessage(e) }),
  })

  const canSave = form.title.trim().length > 0 && !save.isPending

  return (
    <form
      className="contents"
      onSubmit={(e) => {
        e.preventDefault()
        if (canSave) save.mutate()
      }}
    >
      <DialogBody>
        <div className="grid grid-cols-1 gap-x-3 gap-y-2.5 sm:grid-cols-2">
          <Field label={t('documents.meta.documentTitle')} className="sm:col-span-2">
            <Input
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              required
              autoFocus
            />
          </Field>

          <Field label={t('documents.meta.shortName')}>
            <Input value={form.short_name} onChange={(e) => set('short_name', e.target.value)} />
          </Field>
          <Field label={t('documents.meta.type')}>
            <NativeSelect
              value={form.document_type}
              onChange={(e) => set('document_type', e.target.value as DocumentType)}
            >
              {DOCUMENT_TYPES.map((v) => (
                <option key={v} value={v}>
                  {typeLabel(v)}
                </option>
              ))}
            </NativeSelect>
          </Field>

          <Field label={t('documents.meta.author')}>
            <Input value={form.author} onChange={(e) => set('author', e.target.value)} />
          </Field>
          <Field label={t('documents.meta.publisher')}>
            <Input value={form.publisher} onChange={(e) => set('publisher', e.target.value)} />
          </Field>

          <Field label={t('documents.meta.publication')}>
            <Input value={form.publication} onChange={(e) => set('publication', e.target.value)} />
          </Field>
          <Field label={t('documents.meta.year')}>
            <Input
              type="number"
              inputMode="numeric"
              min={1500}
              max={2100}
              value={form.publication_year}
              onChange={(e) => set('publication_year', e.target.value)}
            />
          </Field>

          <Field label={t('documents.meta.languages')} hint={t('documents.meta.languagesHint')}>
            <Input value={form.languages} onChange={(e) => set('languages', e.target.value)} />
          </Field>
          <Field label={t('documents.meta.isTranslation')}>
            <CheckRow
              checked={form.is_translation}
              onChange={(v) => set('is_translation', v)}
              label={t('documents.meta.isTranslationLabel')}
            />
          </Field>

          <Field label={t('documents.meta.url')} className="sm:col-span-2">
            <Input
              type="url"
              value={form.url}
              onChange={(e) => set('url', e.target.value)}
              placeholder="https://"
            />
          </Field>

          <Field label={t('documents.meta.dateAccessed')}>
            <Input
              type="date"
              value={form.date_accessed}
              onChange={(e) => set('date_accessed', e.target.value)}
            />
          </Field>
          <Field label={t('documents.meta.licence')} hint={t('documents.meta.licenceHint')}>
            <Input value={form.licence} onChange={(e) => set('licence', e.target.value)} />
          </Field>

          <Field label={t('documents.meta.validity')}>
            <CheckRow
              checked={form.is_valid}
              onChange={(v) => set('is_valid', v)}
              label={t('documents.meta.isValid')}
            />
          </Field>
          <Field label={t('documents.meta.validUntil')} hint={t('documents.meta.validUntilHint')}>
            <Input
              type="date"
              value={form.valid_until}
              onChange={(e) => set('valid_until', e.target.value)}
            />
          </Field>

          <Field label={t('documents.meta.comments')} className="sm:col-span-2">
            <Textarea
              value={form.comments}
              onChange={(e) => set('comments', e.target.value)}
              rows={3}
            />
          </Field>
        </div>

        {/* The source table's first column and the upload date: facts about
            the file, not editable. */}
        <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 border-t border-border pt-2 text-[11px] text-muted-foreground">
          <dt>{t('documents.meta.filename')}</dt>
          <dd className="truncate" title={doc.filename ?? ''}>
            {doc.has_file ? (
              <a
                href={documentFileUrl(doc.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                {doc.filename}
              </a>
            ) : (
              doc.filename || '—'
            )}
          </dd>
          <dt>{t('documents.meta.added')}</dt>
          <dd>{doc.date_created ? formatDate(doc.date_created) : '—'}</dd>
        </dl>
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" size="sm" disabled={!canSave}>
          {save.isPending && <Loader2 className="animate-spin" />}
          {t('common.save')}
        </Button>
      </DialogFooter>
    </form>
  )
}

function Field({
  label,
  hint,
  className,
  children,
}: {
  label: string
  hint?: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <label className={cn('block', className)}>
      <span className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {children}
      {hint && <span className="mt-0.5 block text-[10px] text-muted-foreground">{hint}</span>}
    </label>
  )
}

/** A checkbox sitting at input height, so it lines up with its row-mate. */
function CheckRow({
  checked,
  onChange,
  label,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  label: string
}) {
  return (
    <span className="flex h-8 cursor-pointer items-center gap-2 text-sm">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(v === true)} />
      {label}
    </span>
  )
}
